// Slice-7 take-family gate — decides which surface serves /assessments/:id/*
// for take-family contexts. R15b-b3: the 'v1-take' fail-close is RETIRED with
// the v1 tree; every former v1 cell now has a native disposition:
//   - teacher PREVIEW (store-flip apPreviewTest) → 'v4-preview' for EVERY
//     teacher-side preview — delivery-mode-agnostic (dev 2026-07-23: paper
//     gets the same online-style preview; Export Assessment owns paper), and
//     one-by-one (native pager) renders inside the preview surface post-fetch,
//     so the gate no longer needs a hydrated store.
//   - real STUDENTS and GUEST students → 'v4-take' (incl. mobile-upload
//     sessions — the native host owns upload mode since MU1).
//   - direct-login PARENTS → 'v4-take' (the host's view machine dispatches
//     exactly like v1 ExperienceTake — viewFeedback for assessment+EU).
//   - student REVIEWER context → 'v4-reviewer'.
//   - teacher preview-as-student (apTakeTest): NO live producer sets the
//     store to apTakeTest anymore (repo-wide grep, R15b-b3) — mapped to
//     'v4-take' defensively (same shared take machine v1 mounted).
//   - unresolved role (auth hydrating) → 'pending-role' (spinner, re-gates
//     when the store settles; v1 rendered the v1 tree's own spinner).
//   - embedded teacher-side (non-reviewer) contexts → 'embed-view' (J22-B02,
//     v3 parity: the MB pane rendered the read-only ExperienceManage
//     presentation — embedded ExperienceShow defaulted to apEditTest with
//     editAllowed and every header action gated on `!embedded`).
//   - embedded reviewer / unresolved-role contexts → 'embed-unsupported'
//     (dev KILL 2026-07-08(h); graceful notice).
// Dependency-free on purpose: routes.tsx imports this directly without
// pulling any take-page chunk.

export type TakeSurface =
  | 'v4-preview'
  | 'v4-take'
  | 'v4-reviewer'
  | 'pending-role'
  | 'embed-view'
  | 'embed-unsupported'
  | null;

export interface TakeSurfaceArgs {
  role?: string;
  embedded: boolean;
  experienceViewMode?: string | null;
  /** URL `mobileUploadMode=true` — the phone-side upload session (rides the
   *  native host's useMobileUploadSession for every role since R15b-b3). */
  mobileUploadSession?: boolean;
  /** PV2-reviewer-seat: pathname includes '/grade' OR the URL carries a
   *  `reviewer` param (v3 ReviewButton lands `/grade?reviewer=true`). */
  reviewerContext?: boolean;
}

/**
 * null = not a take/preview context — the caller continues with its tab logic.
 */
export const resolveTakeSurface = ({
  role,
  embedded,
  experienceViewMode,
  mobileUploadSession = false,
  reviewerContext = false,
}: TakeSurfaceArgs): TakeSurface => {
  // EMBEDDED (EM1-embedded, QUEUE 3.10e): MB-embedded STUDENTS and PARENTS
  // ride the v4 take host in its embed chrome variant. Teacher-side roles get
  // the read-only presentation view (J22-B02, v3 parity) unless the preview
  // store flip is active; embedded reviewer contexts stay on the dev KILL;
  // embedded mobile-upload sessions ride the same native host (unreachable
  // inside the MB iframe in practice — defensive).
  if (embedded) {
    if ((role === 'student' || role === 'parent') && !reviewerContext) {
      return 'v4-take';
    }
    if (!role) return 'pending-role';
    const teacherSide = role !== 'student' && role !== 'parent';
    if (teacherSide && experienceViewMode === 'apPreviewTest') return 'v4-preview';
    if (teacherSide && !reviewerContext) return 'embed-view';
    return 'embed-unsupported';
  }

  // PARENTS' RESULTS FLIP (PA1-parents, QUEUE 3.11).
  if (role === 'parent') return 'v4-take';

  // THE STUDENT TAKE FLIP (FL1-flip, QUEUE 3.7; guests since GS1).
  if (role === 'student') {
    if (reviewerContext) return 'v4-reviewer';
    return 'v4-take';
  }

  // Teacher preview-as-student — dead cell (no live apTakeTest producer);
  // defensively the shared take machine, exactly what v1 mounted.
  if (experienceViewMode === 'apTakeTest') return 'v4-take';

  // No resolved role (auth still hydrating / token refresh) — wait, re-gate.
  // P2-13 residual: MUST run before the viewMode gate — on a cold load the
  // store hasn't flipped apPreviewTest yet either, and returning null here
  // would let the caller run its tab logic role-less instead of spinning.
  if (!role) return 'pending-role';

  if (experienceViewMode !== 'apPreviewTest') return null;

  // Teacher-side preview — delivery-mode-agnostic (dev 2026-07-23); the
  // one-by-one pager renders inside the preview surface post-fetch.
  return 'v4-preview';
};

/**
 * OP1-onebyone (QUEUE 3.8) post-fetch fail-close matrix, ExperienceTakePage —
 * STUDENT branch. R15b-b3 dispositions: drafts render the native unauthorized
 * ErrorView (v1's screen, byte-equal copy); a reviewer-context hit (defense in
 * depth — the route gate routes these to ExperienceReviewerPage) renders the
 * native reviewer page.
 */
export const studentTakeFailClose = (
  experience: { status?: string; settings?: { peer_review_enabled?: boolean } } | null,
  enabledFeatures: { allow_peer_review_assessment?: boolean },
  reviewerContext = false
): boolean =>
  (!!enabledFeatures?.allow_peer_review_assessment &&
    !!experience?.settings?.peer_review_enabled &&
    reviewerContext) ||
  experience?.status === 'draft';

/**
 * PA1-parents (QUEUE 3.11) post-fetch check, ExperienceTakePage: parents only
 * ever reach viewFeedback via the view machine (assessment + child EU). A
 * learning experience or an EU-less deep link dispatches a take surface —
 * exactly what v1 ExperienceTake did; since R15b-b3 the native host serves it
 * (same machine), so this check gates nothing fatal anymore. Kept exported
 * for the draft gate composition in ExperienceTakePage.
 */
export const parentTakeFailClose = (
  experience: {
    experience_type?: string;
    experience_user?: unknown;
  } | null
): boolean => experience?.experience_type !== 'assessment' || !experience?.experience_user;
